/**
 * The range token is a path segment on the dashboard endpoints. `AllTime` is the default and
 * resolves server-side to an unbounded window with no comparison period.
 */
export type DashboardRange = 'AllTime' | 'Days30' | 'Days60' | 'Days90'

export const DASHBOARD_RANGE_OPTIONS: { value: DashboardRange; label: string }[] = [
  { value: 'AllTime', label: 'All Time' },
  { value: 'Days30', label: 'Last 30 Days' },
  { value: 'Days60', label: 'Last 60 Days' },
  { value: 'Days90', label: 'Last 90 Days' },
]

/**
 * A count for the current window. `Previous` is null on All Time, where there is no preceding
 * period — so a delta must be hidden rather than rendered as zero.
 */
export interface DashboardWindowCountDto {
  Current: number
  Previous?: number | null
}

export interface DashboardNewestConversionItemDto {
  /** Campaign-scoped lead id — pairs with CampaignId for the campaign-scoped contact endpoint. */
  LeadId: string
  CampaignId: string
  /**
   * The workspace representative lead, which is what the workspace contact endpoint matches.
   * Workspace scope only, and absent when the identity has no representative — a lead carrying no
   * email address never enters the deduped workspace set. Absent means there is no workspace
   * profile to open, so the row is not activatable there.
   */
  WorkspaceLeadId?: string | null
  FirstName?: string | null
  LastName?: string | null
  Name?: string | null
  CompanyName?: string | null
  ConvertedAtUtc: string
  ProfileImageUrl?: string | null
}

export interface DashboardHottestCompanyItemDto {
  /** Canonical Company document id — NOT the email-domain id used by the Companies grids. */
  CompanyId: string
  CompanyName?: string | null
  LogoUrl?: string | null
  PrimaryIndustry?: string | null
  StaffCountRange?: string | null
  ConversionCount: number
  LastConversionUtc?: string | null
}

export interface DashboardHottestLeadItemDto {
  LeadId: string
  ContactId?: string | null
  FirstName?: string | null
  LastName?: string | null
  Name?: string | null
  JobTitle?: string | null
  CompanyName?: string | null
  SignalScore: number
  SignalLevel?: string | null
  LastInteractionAt?: string | null
  ProfileImageUrl?: string | null
}

export interface DashboardTopPerformingWebItemDto {
  WebId: string
  CampaignId: string
  Name: string
  Title: string
  Type: string
  LeadGoal: number
  LeadsGenerated: number
}

export interface CampaignTrendPointDto {
  DateUtc: string
  CumulativeLeadCount: number
}

export interface DashboardCampaignPerformanceItemDto {
  CampaignId: string
  CampaignName: string
  LeadsGenerated: number
  CompaniesEngaged: number
  TopWebId?: string | null
  TopWebName?: string | null
  TopWebSubmissionCount: number
  LeadTrend: CampaignTrendPointDto[]
}

/**
 * Each section is null when it could not be computed, and is then named in `FailedSections`.
 * A null section means "broken", not "empty" — render it as an error, not as a zero.
 */
export interface DashboardTileSectionsDto {
  TotalLeadsGenerated?: DashboardWindowCountDto | null
  CompaniesEngaged?: DashboardWindowCountDto | null
  /** Workspace scope only; null when scoped to a campaign. */
  ActiveCampaignCount?: number | null
  ActiveWebCount?: number | null
  NewestConversions?: DashboardNewestConversionItemDto[] | null
  HottestCompanies?: DashboardHottestCompanyItemDto[] | null
  HottestLeads?: DashboardHottestLeadItemDto[] | null
  TopPerformingWebs?: DashboardTopPerformingWebItemDto[] | null
  /** Workspace scope only; null when scoped to a campaign. */
  CampaignPerformance?: DashboardCampaignPerformanceItemDto[] | null
  FailedSections: string[]
}

/** Every tile on a dashboard, from one server-side read. */
export interface DashboardTilesDto {
  WorkspaceId: string
  CampaignId?: string | null
  Range: DashboardRange
  RangeDays?: number | null
  CurrentWindowStartUtc?: string | null
  PreviousWindowStartUtc?: string | null
  GeneratedAtUtc: string
  Limit: number
  Sections: DashboardTileSectionsDto
}
